// Copyright Epic Games, Inc. All Rights Reserved.
import type { MDXRemoteSerializeResult } from 'next-mdx-remote';
import type { LocalizationContextType } from '@epic-mw/localization';
import { DEFAULT_LOCALE } from '@epic-mw/localization';
import type { SSRConfig } from 'next-i18next';
import type { GetStaticPaths, GetStaticProps } from 'next';

import type { ActiveDocMeta, NavTree } from '@models/NavigationTree';
import { getUrlPathFromSlug, getFlattenedPath } from '@utils/common';
import type { SeoConfig } from '@utils/seo';
import type { LocalesUnion } from '@constants/i18n';
import { LOCALES_ARR } from '@constants/i18n';
import type { PublicSettingsDto } from '@dto/PublicSettings.dto';
import type { Document } from '@models/Document';
import type { PartialsMap } from '@models/Partial';
import type { TableOfContentsMap } from '@models/Toc';
import { withUserGating } from '@guards/UserGating';
import { getBrowserDocURLs } from '@utils/navigation/docs-paths';
import { fetchDocumentPageProps } from '@utils/fetchDocumentPageProps';
import { DocumentPageView } from '@layout/DocumentPageView';

export type DocumentationPageProps = {
  pageSource: MDXRemoteSerializeResult;
  partialsSources: PartialsMap;
  document: Document;
  i18nContext: LocalizationContextType;
  navTree: NavTree;
  activeDocMeta: ActiveDocMeta;
  timeToRead: number;
  publicRuntimeSettings: PublicSettingsDto;
  seoConfig: SeoConfig;
  tableOfContentsMap: TableOfContentsMap;
  userGatingEnabled: boolean;
  isProtected: boolean;
  locale: LocalesUnion;
  notFound: boolean;
} & SSRConfig;

export default withUserGating(DocumentPageView);

export const getStaticProps: GetStaticProps = async ({ params }) => {
  // Extract locale from slug (middleware rewrites /page -> /ko/page)
  // params.slug = ['ko', 'dev-portal', 'intro'] for /docs/ko/dev-portal/intro
  const slug = params?.slug || [];
  const firstSegment = slug[0];

  // Check if first segment is a locale
  const isLocaleInPath = LOCALES_ARR.includes(firstSegment as LocalesUnion);

  // Extract locale and remaining slug
  const targetLocale: LocalesUnion = isLocaleInPath ? (firstSegment as LocalesUnion) : (DEFAULT_LOCALE as LocalesUnion);
  const contentSlug = isLocaleInPath ? slug.slice(1) : slug;

  const urlPath = getUrlPathFromSlug(contentSlug);
  const flattenedPath = getFlattenedPath(urlPath, targetLocale);

  return fetchDocumentPageProps(flattenedPath, targetLocale);
};

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const paths: Array<{ params: { slug: string[] } }> = [];

    // Ephemeral previews prerender nothing and let `fallback: 'blocking'` render each
    // page on first request — trades a one-off render for ~270s off the build. Prod
    // and gamedev still prerender everything.
    if (process.env.PRERENDER_SCOPE === 'none') {
      return {
        paths: [],
        fallback: 'blocking',
      };
    }

    // Get all document URLs from navigation tree
    const allDocUrls = await getBrowserDocURLs();

    // Generate paths for each document and each locale
    allDocUrls.forEach((url) => {
      // Remove leading slash and split into segments
      const segments = url.replace(/^\//, '').split('/').filter(Boolean);

      if (segments.length === 0) {
        return;
      }

      // Locale-prefixed only. Middleware rewrites `/page` to `/{locale}/page` before
      // routing, so a prerendered locale-free path is never served — it was 20% of the
      // generation phase producing output nothing reads.
      LOCALES_ARR.forEach((locale) => {
        paths.push({
          params: {
            slug: [locale, ...segments],
          },
        });
      });
    });

    // Note: Root paths (slug=[] and slug=['locale']) are NOT generated here
    // They are handled by index.tsx with SSR for proper locale detection

    return {
      paths,
      fallback: 'blocking',
    };
  } catch (error) {
    console.error('[getStaticPaths:error]', error);
    return {
      paths: [],
      fallback: 'blocking',
    };
  }
};
